import type { ManifestFile, SkippedFile } from './types';

/**
 * Fetches photos in parallel but yields them strictly in order, with a hard cap
 * on how many bytes may sit in memory at once.
 *
 * client-zip consumes entries sequentially, so any photo that finishes early
 * must be held until its turn. That buffer is the one place a big download can
 * blow up the tab, so it is bounded by bytes — not by file count, which would
 * be meaningless when photos range from 2 MB JPEGs to 45 MB RAWs.
 */

export interface FetchedEntry {
  file: ManifestFile;
  data: Uint8Array;
}

export interface PipelineOptions {
  /** Resolve a fresh presigned URL. Batching/caching lives in the provider. */
  getUrl: (file: ManifestFile, forceRefresh: boolean) => Promise<string | null>;
  concurrency: number;
  /** Max bytes held in the reorder buffer. */
  maxBufferedBytes: number;
  signal: AbortSignal;
  onSkip: (skipped: SkippedFile) => void;
  /** Called when a file's bytes land, before it is yielded. */
  onFetched?: (file: ManifestFile, bytes: number) => void;
}

const RETRY_DELAYS_MS = [500, 2_000, 6_000];

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(t);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}

/**
 * Fetch one photo with retries. The final attempt forces a fresh presigned URL,
 * which is what rescues a download whose URLs expired mid-transfer.
 */
async function fetchOne(
  file: ManifestFile,
  opts: PipelineOptions,
): Promise<Uint8Array> {
  let lastError: unknown;

  for (let attempt = 0; attempt < RETRY_DELAYS_MS.length; attempt++) {
    if (opts.signal.aborted) throw new DOMException('Aborted', 'AbortError');
    try {
      const url = await opts.getUrl(file, attempt === RETRY_DELAYS_MS.length - 1);
      if (!url) throw new Error('No download URL');

      const res = await fetch(url, { signal: opts.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const buf = await res.arrayBuffer();
      if (buf.byteLength === 0) throw new Error('Empty response');
      return new Uint8Array(buf);
    } catch (err) {
      if (opts.signal.aborted) throw err;
      lastError = err;
      if (attempt < RETRY_DELAYS_MS.length - 1) {
        await delay(RETRY_DELAYS_MS[attempt], opts.signal);
      }
    }
  }

  throw lastError instanceof Error ? lastError : new Error(String(lastError));
}

/**
 * Ordered, bounded-memory fetch pipeline.
 *
 * Yields `{ file, data }` in manifest order. A photo that fails all retries is
 * reported via `onSkip` and omitted — one unreadable photo must never fail the
 * whole archive, which was the failure mode of the Lambda this replaces.
 */
export async function* fetchInOrder(
  files: ManifestFile[],
  opts: PipelineOptions,
): AsyncGenerator<FetchedEntry> {
  type Slot = { promise: Promise<Uint8Array | null>; bytes: number; settled: boolean };

  const slots = new Map<number, Slot>();
  let nextToStart = 0;
  let nextToYield = 0;
  let bufferedBytes = 0;

  /** Resolves when a slot settles, so the scheduler can re-evaluate capacity. */
  let wake: (() => void) | null = null;
  const settledSignal = () =>
    new Promise<void>((resolve) => {
      wake = resolve;
    });
  const notify = () => {
    const w = wake;
    wake = null;
    w?.();
  };

  const start = (index: number) => {
    const file = files[index];
    const slot: Slot = { bytes: 0, settled: false, promise: Promise.resolve(null) };
    slot.promise = fetchOne(file, opts)
      .then((data) => {
        slot.bytes = data.byteLength;
        bufferedBytes += data.byteLength;
        slot.settled = true;
        opts.onFetched?.(file, data.byteLength);
        notify();
        return data;
      })
      .catch((err) => {
        slot.settled = true;
        notify();
        if (opts.signal.aborted) throw err;
        opts.onSkip({
          photoId: file.photoId,
          name: file.name,
          proof: file.proof,
          reason: err instanceof Error ? err.message : String(err),
        });
        return null;
      });
    slots.set(index, slot);
  };

  while (nextToYield < files.length) {
    if (opts.signal.aborted) throw new DOMException('Aborted', 'AbortError');

    // Fill the window, bounded two ways:
    //   - at most `concurrency` slots open (in-flight or waiting their turn),
    //     which also bounds the memory held by in-flight arrayBuffer() calls;
    //   - buffered (settled but not yet yielded) bytes under the cap. This is
    //     what stops a fast connection racing ahead of the zip writer and
    //     pulling the whole album into RAM.
    // The `nextToStart === nextToYield` escape guarantees forward progress even
    // when a single photo is larger than the whole buffer budget.
    while (
      nextToStart < files.length &&
      nextToStart - nextToYield < opts.concurrency &&
      (bufferedBytes < opts.maxBufferedBytes || nextToStart === nextToYield)
    ) {
      start(nextToStart);
      nextToStart++;
    }

    const slot = slots.get(nextToYield);
    if (!slot) {
      // Nothing in flight for this index yet — can only happen if the byte cap
      // blocked every start; wait for capacity.
      await settledSignal();
      continue;
    }

    const data = await slot.promise;
    slots.delete(nextToYield);
    if (data) {
      bufferedBytes -= data.byteLength;
      const entry: FetchedEntry = { file: files[nextToYield], data };
      nextToYield++;
      yield entry;
    } else {
      // Skipped — already reported via onSkip.
      nextToYield++;
    }
    notify();
  }
}
